import { useState, useEffect } from "react"
import type { AiSettings } from "@/entities/ai/types"
import { getAiSettings, updateAiSettings, reindexAi } from "./settingsApi"

interface Props {
  workspaceSlug: string
  onClose: () => void
}

const PROVIDERS = [
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
]

const MODELS: Record<string, string[]> = {
  openai: [
    "gpt-4.1",
    "gpt-4.1-mini",
    "gpt-4.1-nano",
    "gpt-4o",
    "gpt-4o-mini",
    "o3-mini",
    "o1",
  ],
  anthropic: [
    "claude-sonnet-4-5-20250514",
    "claude-haiku-4-5-20251001",
    "claude-opus-4-5-20250527",
  ],
}

export function AiSettingsForm({ workspaceSlug, onClose }: Props) {
  const [settings, setSettings] = useState<AiSettings | null>(null)
  const [provider, setProvider] = useState("")
  const [apiKey, setApiKey] = useState("")
  const [model, setModel] = useState("")
  const [saving, setSaving] = useState(false)
  const [reindexing, setReindexing] = useState(false)
  const [message, setMessage] = useState("")

  useEffect(() => {
    getAiSettings(workspaceSlug).then(s => {
      setSettings(s)
      setProvider(s.ai_provider || "openai")
      setModel(s.ai_model || "")
    }).catch(() => {
      setMessage("Failed to load settings (admin access required)")
    })
  }, [workspaceSlug])

  const handleSave = async () => {
    setSaving(true)
    setMessage("")
    try {
      const params: Record<string, string> = {
        ai_provider: provider,
        ai_model: model,
      }
      if (apiKey) params.ai_api_key = apiKey

      await updateAiSettings(workspaceSlug, params)
      setMessage("Settings saved!")
      setApiKey("")
    } catch {
      setMessage("Failed to save settings")
    } finally {
      setSaving(false)
    }
  }

  if (!settings) return <div className="p-4 text-sm text-gray-500">Loading...</div>

  const chatModels = MODELS[provider] || MODELS.openai

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">AI Settings</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">Close</button>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Provider</label>
        <select
          value={provider}
          onChange={e => { setProvider(e.target.value); setModel("") }}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          {PROVIDERS.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          API Key {settings.ai_api_key_set && <span className="text-green-600">(set)</span>}
        </label>
        <input
          type="password"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          placeholder={settings.ai_api_key_set ? "Leave blank to keep current" : "Enter API key"}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Chat Model</label>
        <select
          value={model}
          onChange={e => setModel(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Select model</option>
          {chatModels.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {message && (
        <div className={`text-xs ${message.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
          {message}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save Settings"}
      </button>

      {settings.ai_configured && (
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Indexed: {settings.indexed_pages} / {settings.total_pages} pages
            </span>
            <button
              onClick={async () => {
                setReindexing(true)
                try {
                  await reindexAi(workspaceSlug)
                  setMessage("Reindex started!")
                } catch {
                  setMessage("Failed to start reindex")
                } finally {
                  setReindexing(false)
                }
              }}
              disabled={reindexing}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
            >
              {reindexing ? "Starting..." : "Reindex all"}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
